/* eslint-disable @next/next/no-img-element */
import { omit } from "ramda";
import { useEffect, useState } from "react";

import { useUser } from "src/context/user";

import Loader from "components/general/loader";
import Modal from "components/general/modal";
import { useRouter } from "next/router";
import styled from "styled-components";

import { datadogRum } from "@datadog/browser-rum";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { usePostHog } from "posthog-js/react";
import {
  personalPricingOptions,
  iteratablePricingOptions,
  isSeasonalDiscountActive,
} from "src/data/variables";
import { tokenStore } from "src/hooks/useToken";
import { gtag_report_purchase, gtag_set_user_id } from "src/lib/ga";
import { LoggedInContent } from "./loggedInContent";
import { useAtom } from "jotai";
import { priceVariantAtom } from "src/atoms/pricingPage";

dayjs.extend(localizedFormat);

const StyledWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 2rem;
  font-family: "Open Sans", sans-serif;
  @media screen and (max-width: 920px) {
    margin-top: 3rem;
  }
`;
const StyledContainer = styled.div`
  width: 100%;
  max-width: 1280px;
  display: flex;
  border-radius: 1rem;
  background-color: var(--raised-background-color-three);
  min-height: 720px;
  @media screen and (max-width: 1040px) {
    flex-direction: column;
  }
`;
const StyledLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem 1rem 2rem 2rem;
  box-sizing: border-box;
  @media screen and (max-width: 920px) {
    padding: 2rem;
  }
`;
const StyledRight = styled.div`
  flex: 4;
  background-color: var(--raised-background-color-two);
  display: flex;
  flex-direction: column;
  padding: 2rem;
  text-align: left;
`;
const StyledSubText = styled.div`
  font-size: 12px;
  margin-bottom: 1rem;
  span {
    color: #5885f8;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledSuccessMessage = styled.div`
  padding: 1rem;
  display: flex;
  background: rgb(12, 19, 13);
  gap: 1rem;
  margin-bottom: 2rem;
`;
const StyledPrice = styled.div`
  padding: 1rem 0 1.5rem 0;
  border-top: 1px solid #666;
  border-bottom: 1px solid #666;
  margin-bottom: 1rem;
`;

/**
 * @param {Record<string, string>} query
 *
 * @returns number | undefined
 */
const getMetaPrice = (query) => {
  if ("__meta_price" in query) {
    try {
      const parsed = Number(query["__meta_price"]);
      if (isFinite(parsed)) {
        return parsed;
      }
    } catch (error) {
      return undefined;
    }
  }
};

const SubscriptionPageContent = ({ isSuccessfulPayment }) => {
  const router = useRouter();

  const { userState, refreshUser, loadAffiliateStats } = useUser();

  const [priceVariant] = useAtom(priceVariantAtom);

  const [priceVariantForSidebar, setPriceVariantForSidebar] = useState(
    priceVariant ?? personalPricingOptions.premiumYear.seasonalDiscountFullPrice,
  );

  useEffect(() => {
    loadAffiliateStats(false);
  }, [loadAffiliateStats]);

  const logout = async () => {
    tokenStore.token = null;
    tokenStore.securityToken = null;
    await refreshUser();

    gtag_set_user_id(null);

    router.push("/login");
  };

  const [sentEvent, setSentEvent] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    if (
      isSuccessfulPayment &&
      router.query?.["from"] === "stripe" &&
      userState.user?.id &&
      !sentEvent
    ) {
      console.log("Successful checkout");

      datadogRum.addAction("stripe-successful-payment", {
        id: userState.user.id,
        email: userState.user.email,
      });

      posthog?.capture("stripe-successful-payment", {
        user_id: userState.user.id,
        email: userState.user.email,
      });

      const metaPrice = getMetaPrice(router.query);

      posthog?.capture("purchase", {
        user_id: userState.user.id,
        email: userState.user.email,
        value: metaPrice || 95,
        currency: "USD",
        method: "stripe",
      });

      gtag_report_purchase("stripe", metaPrice || 95);

      setSentEvent(true);
      router.replace(
        {
          pathname: router.pathname,
          query: omit(["from", "__meta_price"], router.query),
        },
        undefined,
        {
          shallow: true,
          scroll: false,
        },
      );
    }
    if (priceVariant) {
      setPriceVariantForSidebar(priceVariant);
    }
  }, [isSuccessfulPayment, userState.user, router, sentEvent, posthog, priceVariant]);

  useEffect(() => {
    if (!userState.initialized) {
      return;
    }
    if (!userState.user) {
      router.push("/login");
    }
  }, [router, userState.user, userState.initialized]);

  const [showCancelSubscriptionDialog, setShowCancelSubscriptionDialog] = useState(false);

  // const paymentPage = true;
  const paymentPage = !userState.user?.subscription?.isActive && !isSuccessfulPayment;

  if (!userState.user) {
    return <Loader />;
  }
  return (
    <StyledWrapper>
      <StyledContainer>
        <StyledLeft>
          <StyledSubText style={{ marginBottom: 0 }}>Logged in as</StyledSubText>
          <h3 style={{ margin: 0 }}>{userState.user.email}</h3>
          <img
            src="/images/happy-redacto.svg"
            alt="happy redacto"
            style={{ margin: "4rem", maxWidth: "200px" }}
          />
          {paymentPage && (
            <StyledPrice>
              <div
                style={{
                  color: "#aaa",
                  marginBottom: "1rem",
                  fontWeight: "700",
                  fontSize: "1.5rem",
                }}
              >
                {`${iteratablePricingOptions.find((x) => x.priceVariant === priceVariantForSidebar)?.selectedText}`}
              </div>
              <div style={{ color: "#5a87f5", fontWeight: "700", fontSize: "2rem" }}>
                {`${
                  isSeasonalDiscountActive
                    ? iteratablePricingOptions.find(
                        (x) => x.priceVariant === priceVariantForSidebar,
                      )?.seasonalDiscountFullPrice
                    : iteratablePricingOptions.find(
                        (x) => x.priceVariant === priceVariantForSidebar,
                      )?.fullPrice
                }`}
              </div>
            </StyledPrice>
          )}
          <StyledSubText>
            <span
              data-action-name="Subscription: Logout"
              onClick={() => {
                logout();
              }}
            >
              Log out
            </span>
          </StyledSubText>
        </StyledLeft>
        <StyledRight>
          {isSuccessfulPayment && (
            <StyledSuccessMessage>
              <svg
                height="24"
                width="24"
                fill="rgb(102, 187, 106)"
                clipRule="evenodd"
                fillRule="evenodd"
                strokeLinejoin="round"
                strokeMiterlimit="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="m11.998 2.005c5.517 0 9.997 4.48 9.997 9.997 0 5.518-4.48 9.998-9.997 9.998-5.518 0-9.998-4.48-9.998-9.998 0-5.517 4.48-9.997 9.998-9.997zm0 1.5c-4.69 0-8.498 3.807-8.498 8.497s3.808 8.498 8.498 8.498 8.497-3.808 8.497-8.498-3.807-8.497-8.497-8.497zm-5.049 8.886 3.851 3.43c.142.128.321.19.499.19.202 0 .405-.081.552-.242l5.953-6.509c.131-.143.196-.323.196-.502 0-.41-.331-.747-.748-.747-.204 0-.405.082-.554.243l-5.453 5.962-3.298-2.938c-.144-.127-.321-.19-.499-.19-.415 0-.748.335-.748.746 0 .205.084.409.249.557z"
                  fillRule="nonzero"
                />
              </svg>
              <div>
                <b>Success</b>
                <div>Your payment was successful!</div>
              </div>
            </StyledSuccessMessage>
          )}
          <LoggedInContent />
        </StyledRight>
      </StyledContainer>
      <Modal
        displayed={showCancelSubscriptionDialog}
        hide={() => setShowCancelSubscriptionDialog(false)}
      />
    </StyledWrapper>
  );
};

export default SubscriptionPageContent;
